import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { DollarSign, TrendingUp, TrendingDown, Wallet, Plus, CreditCard, Download, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Bounce P&L summary strip (Josh 07/15: $35 charged vs real ACH cost) ────
const BouncePnlStrip = ({ getAuthHeaders }) => {
  const [pnl, setPnl] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/bounce-pnl?date_range=ytd`, { headers: getAuthHeaders() });
        if (alive) setPnl(res.data);
      } catch (e) { /* strip is informational — stay quiet on failure */ }
    })();
    return () => { alive = false; };
  }, [getAuthHeaders]);
  if (!pnl) return null;
  const t = pnl.totals || {};
  return (
    <Card>
      <CardContent className="py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span className="font-medium text-slate-700">Bounce P&amp;L (YTD)</span>
        <span className="text-slate-500">Fees charged: <span className="font-semibold text-slate-900">{formatCurrency(t.fees_charged)}</span></span>
        <span className="text-slate-500">ACH cost ({pnl.ach_return_cost_rate}/return): <span className="font-semibold text-red-600">{formatCurrency(t.ach_cost)}</span></span>
        <span className="text-slate-500">Margin: <span className="font-semibold text-green-600">{formatCurrency(t.margin)}</span></span>
        <span className="text-slate-400">{t.returns_count} returns</span>
      </CardContent>
    </Card>
  );
};

const TYPE_BADGES = {
  fee_credit: { label: 'Fee Credit', cls: 'bg-green-100 text-green-700' },
  manual_credit: { label: 'Manual Credit', cls: 'bg-blue-100 text-blue-700' },
  manual_debit: { label: 'Manual Debit', cls: 'bg-red-100 text-red-700' },
  payout: { label: 'Payout', cls: 'bg-purple-100 text-purple-700' },
};

const formatDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(d).split('T')[0];
  }
};

export default function SyndicatorFeeLedger({ embedded = false, syndicatorId = '' }) {
  // 11/03 (Josh: consolidate): rendered as a tab of the syndicator detail page with the syndicator fixed.
  const Wrap = embedded ? React.Fragment : Layout;
  const { user, getAuthHeaders } = useAuth();
  const isAdmin = user?.roles?.includes('admin');

  const [syndicators, setSyndicators] = useState([]);
  const [selectedSyndicator, setSelectedSyndicator] = useState(syndicatorId || '');
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ total_credits: 0, total_payouts: 0, total_debits: 0, available_balance: 0 });
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');

  // Layer 1 — Syndicator search
  const [syndicatorSearch, setSyndicatorSearch] = useState('');
  const [syndicatorDropdownOpen, setSyndicatorDropdownOpen] = useState(false);
  const syndicatorRef = useRef(null);

  // Layer 2 — Deal filter
  const [dealFilter, setDealFilter] = useState('');

  // Modal states
  const [addEntryOpen, setAddEntryOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutPreview, setPayoutPreview] = useState(null);
  const [deals, setDeals] = useState([]);

  // Entry form
  const [entryForm, setEntryForm] = useState({ entry_type: 'manual_credit', deal_id: '', amount: '', note: '', reference_number: '' });
  const [payoutForm, setPayoutForm] = useState({ period_start: '', period_end: '', total_amount: '', reference_number: '', note: '' });
  const [submitting, setSubmitting] = useState(false);

  const headers = useCallback(() => ({ ...getAuthHeaders(), 'X-Tenant': 'live' }), [getAuthHeaders]);

  // Close syndicator dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (syndicatorRef.current && !syndicatorRef.current.contains(e.target)) {
        setSyndicatorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load syndicators list
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/syndications/fee-ledger/syndicators`, { headers: headers() });
        setSyndicators(res.data || []);
        // Non-admin: auto-select own contact
        if (!isAdmin && res.data?.length === 1) {
          setSelectedSyndicator(res.data[0].id);
        }
      } catch (err) {
        console.error(err);
      }
    })();
  }, [headers, isAdmin]);

  // Load deals for entry modal
  useEffect(() => {
    if (!addEntryOpen || !selectedSyndicator) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/deals`, { headers: headers() });
        setDeals(Array.isArray(res.data) ? res.data : res.data?.deals || []);
      } catch (e) { console.error('Failed to load deals:', e); }
    })();
  }, [addEntryOpen, selectedSyndicator, headers]);

  // Load ledger
  const fetchLedger = useCallback(async () => {
    if (!selectedSyndicator) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/syndications/fee-ledger/${selectedSyndicator}`, { headers: headers() });
      setEntries(res.data.entries || []);
      setSummary(res.data.summary || { total_credits: 0, total_payouts: 0, total_debits: 0, available_balance: 0 });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load fee ledger');
    } finally {
      setLoading(false);
    }
  }, [selectedSyndicator, headers]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  // Layer 1 — filtered syndicator list
  const filteredSyndicators = useMemo(() => {
    if (!syndicatorSearch.trim()) return syndicators;
    const q = syndicatorSearch.toLowerCase();
    return syndicators.filter(s =>
      (s.company_name || '').toLowerCase().includes(q) ||
      (s.name || '').toLowerCase().includes(q)
    );
  }, [syndicators, syndicatorSearch]);

  // Layer 2 — unique deals from current ledger entries
  const ledgerDeals = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      if (e.deal_id && e.deal_name && !map.has(e.deal_id)) {
        map.set(e.deal_id, e.deal_name);
      }
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  // Combined filter: type + deal, then reverse for newest first
  const displayed = useMemo(() => {
    const dealQ = dealFilter.toLowerCase();
    return [...entries]
      .filter(e => {
        if (typeFilter && e.entry_type !== typeFilter) return false;
        if (dealQ) {
          // Entries with no deal only show when deal filter is empty
          if (!e.deal_name) return false;
          if (!e.deal_name.toLowerCase().includes(dealQ)) return false;
        }
        return true;
      })
      .reverse();
  }, [entries, typeFilter, dealFilter]);

  // Reset deal filter when syndicator changes
  useEffect(() => { setDealFilter(''); }, [selectedSyndicator]);

  // Add Entry submit
  const handleAddEntry = async () => {
    if (!entryForm.amount || parseFloat(entryForm.amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    if (entryForm.entry_type === 'manual_debit' && !entryForm.note) {
      toast.error('Note is required for debit entries');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/syndications/fee-ledger/entry`, {
        syndicator_id: selectedSyndicator,
        entry_type: entryForm.entry_type,
        deal_id: entryForm.deal_id || null,
        amount: parseFloat(entryForm.amount),
        note: entryForm.note || null,
        reference_number: entryForm.reference_number || null,
      }, { headers: headers() });
      toast.success('Entry added');
      setAddEntryOpen(false);
      setEntryForm({ entry_type: 'manual_credit', deal_id: '', amount: '', note: '', reference_number: '' });
      fetchLedger();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add entry');
    } finally {
      setSubmitting(false);
    }
  };

  // Open payout modal
  const openPayoutModal = async () => {
    try {
      const res = await axios.get(`${API}/syndications/fee-ledger/${selectedSyndicator}/payout-preview`, { headers: headers() });
      setPayoutPreview(res.data);
      setPayoutForm({
        period_start: '',
        period_end: '',
        total_amount: String(res.data.payout_total || 0),
        reference_number: '',
        note: '',
      });
      setPayoutOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load payout preview');
    }
  };

  // Record payout
  const handleRecordPayout = async () => {
    const amt = parseFloat(payoutForm.total_amount);
    if (!amt || amt <= 0) {
      toast.error('Payout amount must be greater than 0');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/syndications/fee-ledger/${selectedSyndicator}/payout`, {
        syndicator_id: selectedSyndicator,
        period_start: payoutForm.period_start || null,
        period_end: payoutForm.period_end || null,
        total_amount: amt,
        reference_number: payoutForm.reference_number || null,
        note: payoutForm.note || null,
        entry_ids: [],
      }, { headers: headers() });
      toast.success('Payout recorded');
      setPayoutOpen(false);
      fetchLedger();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payout');
    } finally {
      setSubmitting(false);
    }
  };

  // Download CSV
  const downloadCSV = async () => {
    try {
      const res = await axios.get(`${API}/syndications/fee-ledger/${selectedSyndicator}/payout-csv`, {
        headers: headers(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'payout.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download CSV');
    }
  };

  return (
    <Wrap>
      <div className="space-y-6" data-testid="fee-ledger-page">
        {!embedded && <BouncePnlStrip getAuthHeaders={getAuthHeaders} />}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{embedded ? 'Fee ledger' : 'Fee Ledger'}</h1>
            <p className="text-sm text-slate-500 mt-1">Fee credits, adjustments and payouts</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && selectedSyndicator && (
              <>
                <Button variant="outline" size="sm" onClick={() => setAddEntryOpen(true)} data-testid="add-entry-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Entry
                </Button>
                <Button variant="outline" size="sm" onClick={openPayoutModal} data-testid="record-payout-btn">
                  <CreditCard className="h-4 w-4 mr-1" /> Record Payout
                </Button>
              </>
            )}
            {selectedSyndicator && (
              <Button variant="outline" size="sm" onClick={downloadCSV} data-testid="download-csv-btn">
                <Download className="h-4 w-4 mr-1" /> Payout CSV
              </Button>
            )}
          </div>
        </div>

        {/* Layer 1 — Syndicator search (admin only; hidden when the syndicator is fixed by the detail page) */}
        {isAdmin && !syndicatorId && (
          <div className="max-w-xs relative" ref={syndicatorRef}>
            <Label className="text-xs text-slate-500">Syndicator</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                value={syndicatorSearch}
                onChange={(e) => {
                  setSyndicatorSearch(e.target.value);
                  setSyndicatorDropdownOpen(true);
                  if (!e.target.value) {
                    setSelectedSyndicator('');
                  }
                }}
                onFocus={() => setSyndicatorDropdownOpen(true)}
                placeholder="Search syndicator..."
                className="pl-9"
                data-testid="syndicator-search"
              />
            </div>
            {syndicatorDropdownOpen && filteredSyndicators.length > 0 && (
              <div
                className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
                data-testid="syndicator-dropdown"
              >
                {filteredSyndicators.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setSelectedSyndicator(s.id);
                      setSyndicatorSearch(s.company_name || s.name || s.id);
                      setSyndicatorDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 transition-colors ${
                      selectedSyndicator === s.id ? 'bg-slate-50 font-medium' : ''
                    }`}
                    data-testid={`syndicator-option-${s.id}`}
                  >
                    {s.company_name || s.name || s.id}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {!selectedSyndicator ? (
          <div className="text-center py-16 text-slate-400">Select a syndicator to view their fee ledger</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">Total Credits</CardTitle>
                  <TrendingUp className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-green-600" data-testid="kpi-total-credits">{formatCurrency(summary.total_credits)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">Total Paid Out</CardTitle>
                  <CreditCard className="h-4 w-4 text-purple-500" />
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold" data-testid="kpi-total-payouts">{formatCurrency(summary.total_payouts)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">Total Debits</CardTitle>
                  <TrendingDown className="h-4 w-4 text-red-500" />
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-red-600" data-testid="kpi-total-debits">{formatCurrency(summary.total_debits)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">Available Balance</CardTitle>
                  <Wallet className="h-4 w-4 text-slate-400" />
                </CardHeader>
                <CardContent>
                  <p className={`text-2xl font-bold ${summary.available_balance >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="kpi-available-balance">
                    {formatCurrency(summary.available_balance)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Pending payout banner */}
            {summary.available_balance > 0 && (
              <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="pending-payout-banner">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">{formatCurrency(summary.available_balance)}</span> available for payout
                </p>
              </div>
            )}

            {/* Filters — Layer 2 deal filter + type filter */}
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1 min-w-[200px]">
                <label className="text-xs font-medium text-slate-500">Filter by deal</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <Input
                    value={dealFilter}
                    onChange={(e) => setDealFilter(e.target.value)}
                    placeholder="Deal name..."
                    className="pl-9"
                    data-testid="deal-filter"
                    list="deal-filter-options"
                  />
                  <datalist id="deal-filter-options">
                    {ledgerDeals.map((d) => (
                      <option key={d.id} value={d.name} />
                    ))}
                  </datalist>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-500">Entry type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="type-filter"
                >
                  <option value="">All Types</option>
                  <option value="fee_credit">Fee Credit</option>
                  <option value="manual_credit">Manual Credit</option>
                  <option value="manual_debit">Manual Debit</option>
                  <option value="payout">Payout</option>
                </select>
              </div>
              <span className="text-xs text-slate-400 self-end pb-2">{displayed.length} entries</span>
            </div>

            {/* Ledger Table */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : displayed.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">No ledger entries found</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Deal</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Note</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">Running Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayed.map((e, idx) => {
                          const badge = TYPE_BADGES[e.entry_type] || { label: e.entry_type, cls: 'bg-gray-100 text-gray-700' };
                          return (
                            <TableRow key={e.id || idx} data-testid={`ledger-row-${idx}`}>
                              <TableCell className="whitespace-nowrap text-sm">{formatDate(e.date)}</TableCell>
                              <TableCell className="text-sm">{e.deal_name || '-'}</TableCell>
                              <TableCell>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>
                                  {badge.label}{e.source === 'computed' ? ' · computed' : ''}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm text-slate-500 max-w-[250px] truncate" title={e.note || ''}>{e.note || '-'}</TableCell>
                              <TableCell className={`text-right text-sm font-medium ${e.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {e.amount >= 0 ? formatCurrency(e.amount) : `-${formatCurrency(Math.abs(e.amount))}`}
                              </TableCell>
                              <TableCell className={`text-right text-sm font-semibold ${e.running_balance >= 0 ? '' : 'text-red-600'}`}>
                                {e.running_balance >= 0 ? formatCurrency(e.running_balance) : `-${formatCurrency(Math.abs(e.running_balance))}`}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Add Entry Modal */}
        <Dialog open={addEntryOpen} onOpenChange={setAddEntryOpen}>
          <DialogContent data-testid="add-entry-modal" aria-describedby="add-entry-desc">
            <DialogHeader>
              <DialogTitle>Add Manual Entry</DialogTitle>
              <p id="add-entry-desc" className="text-sm text-slate-500">Create a manual credit or debit entry</p>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Entry Type</Label>
                <select
                  value={entryForm.entry_type}
                  onChange={(e) => setEntryForm({ ...entryForm, entry_type: e.target.value })}
                  className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="entry-type-select"
                >
                  <option value="manual_credit">Manual Credit</option>
                  <option value="manual_debit">Manual Debit</option>
                </select>
              </div>
              <div>
                <Label>Deal (optional)</Label>
                <select
                  value={entryForm.deal_id}
                  onChange={(e) => setEntryForm({ ...entryForm, deal_id: e.target.value })}
                  className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="entry-deal-select"
                >
                  <option value="">None</option>
                  {deals.map((d) => (
                    <option key={d.id} value={d.id}>{d.deal_name || d.id}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={entryForm.amount}
                  onChange={(e) => setEntryForm({ ...entryForm, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="entry-amount-input"
                />
              </div>
              <div>
                <Label>Note {entryForm.entry_type === 'manual_debit' && <span className="text-red-500">*</span>}</Label>
                <Input
                  value={entryForm.note}
                  onChange={(e) => setEntryForm({ ...entryForm, note: e.target.value })}
                  placeholder="Reason for entry..."
                  data-testid="entry-note-input"
                />
              </div>
              <div>
                <Label>Reference Number</Label>
                <Input
                  value={entryForm.reference_number}
                  onChange={(e) => setEntryForm({ ...entryForm, reference_number: e.target.value })}
                  placeholder="Optional"
                  data-testid="entry-ref-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddEntryOpen(false)}>Cancel</Button>
              <Button onClick={handleAddEntry} disabled={submitting} data-testid="entry-submit-btn">
                {submitting ? 'Saving...' : 'Add Entry'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Record Payout Modal */}
        <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
          <DialogContent data-testid="payout-modal" aria-describedby="payout-desc">
            <DialogHeader>
              <DialogTitle>Record Payout</DialogTitle>
              <p id="payout-desc" className="text-sm text-slate-500">Record a payout to this syndicator</p>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {payoutPreview && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
                  <p>Available balance: <span className="font-semibold">{formatCurrency(payoutPreview.available_balance)}</span></p>
                  <p>Pending entries: {payoutPreview.pending_entry_count}</p>
                  {payoutPreview.deal_breakdown?.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                      <p className="font-medium text-slate-700">Deal breakdown:</p>
                      {payoutPreview.deal_breakdown.map((d) => (
                        <p key={d.deal_name} className="text-slate-500 pl-2">{d.deal_name}: {formatCurrency(d.total)}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Period Start</Label>
                  <Input
                    type="date"
                    value={payoutForm.period_start}
                    onChange={(e) => setPayoutForm({ ...payoutForm, period_start: e.target.value })}
                    data-testid="payout-start-input"
                  />
                </div>
                <div>
                  <Label>Period End</Label>
                  <Input
                    type="date"
                    value={payoutForm.period_end}
                    onChange={(e) => setPayoutForm({ ...payoutForm, period_end: e.target.value })}
                    data-testid="payout-end-input"
                  />
                </div>
              </div>
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={payoutForm.total_amount}
                  onChange={(e) => setPayoutForm({ ...payoutForm, total_amount: e.target.value })}
                  data-testid="payout-amount-input"
                />
              </div>
              <div>
                <Label>Reference Number</Label>
                <Input
                  value={payoutForm.reference_number}
                  onChange={(e) => setPayoutForm({ ...payoutForm, reference_number: e.target.value })}
                  placeholder="Check/wire reference"
                  data-testid="payout-ref-input"
                />
              </div>
              <div>
                <Label>Note</Label>
                <Input
                  value={payoutForm.note}
                  onChange={(e) => setPayoutForm({ ...payoutForm, note: e.target.value })}
                  placeholder="Optional"
                  data-testid="payout-note-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayoutOpen(false)}>Cancel</Button>
              <Button onClick={handleRecordPayout} disabled={submitting} data-testid="payout-submit-btn">
                {submitting ? 'Processing...' : 'Record Payout'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Wrap>
  );
}
